/**
 * useDrafts hook - Manages draft state for the Create page.
 * Handles saving, listing, restoring, and deleting drafts.
 */

import { useState, useCallback } from 'react';
import { draftsApi, type SaveDraftRequest } from '@/services/draftsApi';
import type { DraftSummary, DraftDetail } from '@/lib/types';
import { createLogger } from '@/lib/logger';

const log = createLogger('useDrafts');

export interface RestoredDraft {
  inputMethod: string;
  languageCode: string;
  audioBlobs: Array<{ blob: Blob; duration: number; contentType: string }>;
  photos: Array<{ blob: Blob; timestamp: number }>;
  notes: Array<{ id: string; text: string; timestamp: number }>;
  streamingTranscripts: Array<{ transcript: string; duration: number }>;
}

export function useDrafts() {
  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

  const loadDrafts = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await draftsApi.listDrafts();
      setDrafts(result);
      setHasAttemptedLoad(true);
    } catch (err) {
      log.error('Failed to load drafts', err as Error);
      // Don't set hasAttemptedLoad on failure — keeps the panel in its
      // initial state so the next load attempt can populate it.
    } finally {
      setIsLoading(false);
    }
  }, []);

  const saveDraft = useCallback(async (
    items: SaveDraftRequest['items'],
    inputMethod: string,
    languageCode: string,
    audioBlobs?: Blob[],
    photoBlobs?: Array<{ blob: Blob; timestamp: number }>,
  ): Promise<string | null> => {
    setIsSaving(true);
    try {
      const now = new Date();
      const title = `Draft — ${now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;

      const response = await draftsApi.saveDraft({
        title,
        inputMethod,
        languageCode,
        items,
      });

      // Upload audio blobs if presigned URLs provided
      if (audioBlobs && response.uploadUrls.audios) {
        await Promise.all(
          audioBlobs.map(async (blob, index) => {
            const url = response.uploadUrls.audios![index];
            if (url) {
              const contentType = items.audios[index]?.contentType || 'audio/webm';
              await draftsApi.uploadBlob(url, blob, contentType);
            }
          })
        );
      }

      // Upload photo blobs if presigned URLs provided
      if (photoBlobs && response.uploadUrls.photos) {
        await Promise.all(
          photoBlobs.map(async (photo, index) => {
            const url = response.uploadUrls.photos![index];
            if (url) {
              const contentType = items.photos[index]?.contentType || 'image/jpeg';
              await draftsApi.uploadBlob(url, photo.blob, contentType);
            }
          })
        );
      }

      log.info('Draft saved', { draftId: response.draftId });

      // Refresh drafts list
      await loadDrafts();

      return response.draftId;
    } catch (err) {
      log.error('Failed to save draft', err as Error);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [loadDrafts]);

  const restoreDraft = useCallback(async (draftId: string): Promise<RestoredDraft> => {
    try {
      const detail: DraftDetail = await draftsApi.getDraft(draftId);

      // Download audio blobs
      const audioBlobs = await Promise.all(
        (detail.items.audios || []).map(async (audio) => {
          const audioResponse = await fetch(audio.downloadUrl);
          const blob = await audioResponse.blob();
          return { blob, duration: audio.duration, contentType: audio.contentType };
        })
      );

      // Download photo blobs
      const photos = await Promise.all(
        (detail.items.photos || []).map(async (photo) => {
          const photoResponse = await fetch(photo.downloadUrl);
          const blob = await photoResponse.blob();
          return { blob, timestamp: photo.timestamp };
        })
      );

      log.info('Draft restored', { draftId, audios: audioBlobs.length, photos: photos.length });

      return {
        inputMethod: detail.inputMethod,
        languageCode: detail.languageCode,
        audioBlobs,
        photos,
        notes: detail.items.notes || [],
        streamingTranscripts: detail.items.streamingTranscripts || [],
      };
    } catch (err) {
      log.error('Failed to restore draft', err as Error);
      throw err;
    }
  }, []);

  const deleteDraft = useCallback(async (draftId: string) => {
    try {
      await draftsApi.deleteDraft(draftId);
      setDrafts(prev => prev.filter(d => d.id !== draftId));
      log.info('Draft deleted', { draftId });
    } catch (err) {
      log.error('Failed to delete draft', err as Error);
      throw err;
    }
  }, []);

  return {
    drafts,
    isLoading,
    isSaving,
    hasAttemptedLoad,
    loadDrafts,
    saveDraft,
    restoreDraft,
    deleteDraft,
  };
}
